/**
 * Send shape to TAK Server.
 * Dispatches to the appropriate CoT XML builder based on tool type.
 */

import type { DrawingTool, GeoPoint } from '@vtak/shared';
import {
  buildPolygonCotXml,
  buildRectangleCotXml,
  buildFreeformCotXml,
  buildCircleCotXml,
  buildRouteCotXml,
  buildGeofenceCotXml,
  buildMeasurementCotXml,
  rectangleFromCorners,
  haversineDistance,
  bearingBetween,
} from '@vtak/shared';
import type { ShapeConfig } from '@/stores/drawing-store';
import type { RouteWaypoint } from '@vtak/shared';
import { apiClient } from '@/services/api-client';

/** Result of a send operation */
interface SendResult {
  ok: boolean;
  error: string;
}

/** Build XML and send shape to TAK based on the active tool */
export async function sendShapeToTak(
  tool: DrawingTool,
  vertices: GeoPoint[],
  radiusMeters: number,
  config: ShapeConfig,
): Promise<SendResult> {
  try {
    const uid = generateUid(tool);
    const xml = buildXmlForTool(tool, uid, vertices, radiusMeters, config);
    const result = await apiClient.sendCot(xml);
    return { ok: result.ok, error: result.ok ? '' : `TAK returned ${result.status}` };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/** Build CoT XML for the given tool type */
function buildXmlForTool(
  tool: DrawingTool,
  uid: string,
  vertices: GeoPoint[],
  radiusMeters: number,
  config: ShapeConfig,
): string {
  const base = { uid, callsign: config.callsign || uid, strokeColor: config.strokeColor, strokeWeight: config.strokeWeight, fillColor: config.fillColor };

  const BUILDERS = new Map<DrawingTool, () => string>([
    ['polygon', () => buildPolygonCotXml({ ...base, vertices })],
    ['rectangle', () => buildRectangleCotXml({ ...base, vertices: rectangleFromCorners(vertices[0]!, vertices[1]!) })],
    ['circle', () => buildCircleCotXml({ ...base, center: vertices[0]!, radiusMeters })],
    ['freeform', () => buildFreeformCotXml({ ...base, vertices })],
    ['route', () => buildRouteCotXml({ ...base, waypoints: buildWaypoints(vertices), planningMethod: config.planningMethod, routeMethod: config.routeMethod, direction: config.direction, routeType: config.routeType })],
    ['geofence', () => buildGeofenceCotXml({ ...base, center: vertices[0]!, radiusMeters, trigger: config.trigger, monitor: config.monitor, tracking: config.tracking, elevationMonitored: config.elevationMonitored, minElevation: config.minElevation, maxElevation: config.maxElevation })],
    ['measurement', () => buildMeasurementCotXml({ ...base, origin: vertices[0]!, endpoint: vertices[1]!, rangeMeters: haversineDistance(vertices[0]!, vertices[1]!), bearingDegrees: bearingBetween(vertices[0]!, vertices[1]!), rangeUnits: config.rangeUnits, bearingUnits: config.bearingUnits, northRef: config.northRef })],
  ]);

  const builder = BUILDERS.get(tool);
  if (!builder) throw new Error(`No builder for tool: ${tool}`);
  return builder();
}

/** Build route waypoints from vertices with standard naming */
function buildWaypoints(vertices: GeoPoint[]): RouteWaypoint[] {
  return vertices.map((v, i) => ({
    uid: `wp-${generateShortId()}`,
    callsign: resolveWaypointName(i, vertices.length),
    type: 'b-m-p-w',
    position: v,
  }));
}

/** Determine waypoint name based on position */
function resolveWaypointName(index: number, total: number): string {
  if (index === 0) return 'SP';
  if (index === total - 1) return 'VDO';
  return `CP${index}`;
}

/** Generate a unique ID for a shape */
function generateUid(tool: DrawingTool): string {
  return `vtak-${tool}-${generateShortId()}`;
}

/** Generate a short random hex ID */
function generateShortId(): string {
  return Date.now().toString(36) + Math.floor(Math.random() * 1000).toString(36);
}
